import ContentSection from '../LandingPage/ContentSection';
import styled from 'styled-components';
import { useParams, Navigate, Link } from 'react-router-dom';
import { seoPages } from './featurePagesContent';
import TopBarInLandingPage from '../../components/common/TopBar/TopBarInLandingPage';
import { useEffect } from 'react';
import GeneratorInLandingPage from '../LandingPage/GeneratorInLandingPage';
import FAQ from '../LandingPage/FAQ';
import { TitleTextWithGradient } from '../LandingPage/CommonComponents';
import Footer from '../../components/Footer/Footer';
import { Helmet } from 'react-helmet';
import {
  BASE_URL,
  FEED_BASE_URL,
  NICE_BG_FOR_CARDS,
  NICE_BG_FOR_CARDS_HOVER,
  PRIMARY_TEXT_COLOR,
} from '../../constants';
import { FaArrowRight } from 'react-icons/fa';
import CommunityImagesSection from '../LandingPage/CommunityImagesSection';
import { mobileMediaQuery } from '../../styleHelpers';
import NewCTAButton from '../LandingPage/NewCTAButton';

const SeoDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const page = slug ? seoPages.find((p) => p.slug === slug) : undefined;

  if (!page) {
    return <Navigate to="/features" replace />;
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const channelNameToLinkTo = page?.channelNameToLinkTo;
  const title = `The Uncensored.AI | ${page.title}`;
  const description = `The Uncensored.AI | ${page.description}`;

  return (
    <Container>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${BASE_URL}/features/${slug}`} />
      </Helmet>
      <TopBarInLandingPage />
      <TitleTextWithGradient>{page.title}</TitleTextWithGradient>
      <PageDescription>{page.description}</PageDescription>

      <NewCTAButton />

      <GeneratorInLandingPage />

      {page.includeCommunityImagesSection && <CommunityImagesSection />}

      {channelNameToLinkTo && (
        <ChannelLinkContainer>
          <ChannelLink to={`${FEED_BASE_URL}/${channelNameToLinkTo}`}>
            <span>
              Browse community images on the #{channelNameToLinkTo} channel
            </span>
            <FaArrowRight size={16} />
          </ChannelLink>
        </ChannelLinkContainer>
      )}

      {page.content.map((section, index) => (
        <ContentSection
          key={`${section.title}-${index}`}
          src={section.src}
          title={section.title}
          description={section.description}
          flipCard={index % 2 === 1}
        />
      ))}

      <FAQ />
      <BrowseOtherFeatures currentSlug={slug || ''} />
      <Footer setShowFirstTimeModal={() => {}} />
    </Container>
  );
};

export default SeoDetail;

const BrowseOtherFeatures: React.FC<{ currentSlug: string }> = ({
  currentSlug,
}) => {
  const otherFeatures = seoPages
    .filter((page) => page.slug !== currentSlug)
    .sort(() => Math.random() - 0.5)
    .slice(0, 5);

  return (
    <OtherFeaturesContainer>
      <OtherFeaturesTitle>Browse Other Features</OtherFeaturesTitle>
      <FeatureGrid>
        {otherFeatures.map((feature) => (
          <FeatureLink key={feature.slug} to={`/features/${feature.slug}`}>
            {feature.title} <FaArrowRight size={12} />
          </FeatureLink>
        ))}
      </FeatureGrid>
    </OtherFeaturesContainer>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 16px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageDescription = styled.p`
  font-size: 18px;
  text-align: left;
  max-width: 800px;
  margin-bottom: 48px;
  line-height: 1.6;

  ${mobileMediaQuery} {
    margin-bottom: 2px;
    margin-top: -24px;
  }
`;

const OtherFeaturesContainer = styled.section`
  width: 100%;
  max-width: 800px;
  margin: 48px 0;
  padding: 0 16px;
`;

const OtherFeaturesTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 24px;
  text-align: center;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

const FeatureLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  color: inherit;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  svg {
    margin-left: 8px;
  }
`;

const ChannelLinkContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 32px 0;
  display: flex;
  justify-content: center;
`;

const ChannelLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 32px;
  background: ${NICE_BG_FOR_CARDS};
  border-radius: 12px;
  color: ${PRIMARY_TEXT_COLOR};
  text-decoration: none;
  font-size: 18px;
  transition: all 0.2s ease;

  &:hover {
    background: ${NICE_BG_FOR_CARDS_HOVER};
    transform: translateY(-2px);
  }
`;
